import MuiAutocomplete, {
  type AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material/Autocomplete';
import { form } from '@/shared/theme/tokens';
import {
  autocompleteFieldCompactSx,
  autocompleteFieldSx,
  selectMenuPaperSx,
} from '../styles/formControlStyles';
import { mergeSx } from '../utils/mergeSx';

/** `default` matches other fields (36px). `compact` is 32px for tight toolbars. */
export type AutocompleteVariant = 'default' | 'compact';

export type AutocompleteProps<
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> = MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> & {
  variant?: AutocompleteVariant;
};

function Autocomplete<
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  size = 'small',
  variant = 'default',
  slotProps,
  sx,
  ...props
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) {
  const paperSlot = slotProps?.paper;
  const paperSlotObject = paperSlot && typeof paperSlot === 'object' ? paperSlot : undefined;
  const paperSlotSx =
    paperSlotObject && 'sx' in paperSlotObject ? paperSlotObject.sx : undefined;

  const listboxSlot = slotProps?.listbox;
  const listboxSlotObject =
    listboxSlot && typeof listboxSlot === 'object' ? listboxSlot : undefined;
  const listboxSlotSx =
    listboxSlotObject && 'sx' in listboxSlotObject ? listboxSlotObject.sx : undefined;

  return (
    <MuiAutocomplete
      size={size}
      sx={mergeSx(
        autocompleteFieldSx,
        variant === 'compact' ? autocompleteFieldCompactSx : undefined,
        sx,
      )}
      slotProps={{
        ...slotProps,
        // Select menus scroll on Paper; Autocomplete already scrolls its Listbox.
        // Keep Paper visual styles but move maxHeight to Listbox to avoid double scrollbars.
        paper: {
          ...paperSlotObject,
          sx: mergeSx(selectMenuPaperSx, { maxHeight: 'none' }, paperSlotSx),
        },
        listbox: {
          ...listboxSlotObject,
          sx: mergeSx({ maxHeight: form.menuMaxHeight }, listboxSlotSx),
        },
      }}
      {...props}
    />
  );
}

export default Autocomplete;
